<template>
  <div class="contact-page">
    <section class="contact-hero">
      <div class="contact-hero__copy">
        <span class="contact-hero__eyebrow">Contact Directory</span>
        <h1>Contact US.</h1>
        <p>
          Find the office address, department leads, and key contact persons in one clear,
          structured directory.
        </p>
      </div>

      <div class="contact-hero__card">
        <span class="hero-card__label">Office Base</span>
        <strong>Federal Ministry of Health</strong>
        <p>
          3rd Floor, New Federal Secretariat Complex, Phase III, Central Business District, FCT
          Abuja, Nigeria.
        </p>
      </div>
    </section>

    <section class="contact-overview">
      <article class="overview-card overview-card--wide">
        <span class="overview-card__label">Primary Address</span>
        <h2>Department of Health Planning, Research and Statistics</h2>
        <p>
          Federal Ministry of Health, 3rd Floor, New Federal Secretariat Complex, Phase III, Central
          Business District, FCT Abuja, Nigeria.
        </p>
      </article>
    </section>

    <section v-for="group in contactGroups" :key="group.department" class="contact-group">
      <div class="contact-group__header">
        <div>
          <span class="contact-group__kicker">Department</span>
          <h2>{{ group.department }}</h2>
        </div>
        <span class="contact-group__count">{{ group.contacts.length }} contacts</span>
      </div>

      <div class="contact-grid">
        <article
          v-for="contact in group.contacts"
          :key="`${group.department}-${contact.name}`"
          class="contact-card"
        >
          <div class="contact-card__identity">
            <img :src="contact.image" :alt="contact.name" class="contact-card__avatar" />

            <div>
              <span class="contact-card__position">{{ contact.position }}</span>
              <h3>{{ contact.name }}</h3>
              <p class="contact-card__department">{{ contact.department }}</p>
            </div>
          </div>

          <dl class="contact-card__meta">
            <div>
              <dt>Phone Number</dt>
              <dd>
                <a :href="`tel:${contact.phone}`">{{ contact.phone }}</a>
              </dd>
            </div>
            <div>
              <dt>Email</dt>
              <dd>
                <a :href="`mailto:${contact.email}`">{{ contact.email }}</a>
              </dd>
            </div>
            <div>
              <dt>Address</dt>
              <dd>{{ contact.address }}</dd>
            </div>
          </dl>
        </article>
      </div>
    </section>
  </div>
</template>

<script>
import userIcon from '@/assets/user-icon.svg';

export default {
  name: 'ContactUsPage',
  data() {
    return {
      contactGroups: [
        {
          department: 'NGO Coordination Branch',
          contacts: [
            {
              name: 'Mr. Michael Ogbonna',
              position: 'Admin 1',
              department: 'NGO Coordination Branch',
              phone: '+2348035810724',
              email: 'micheal.ogbonna@fmoh.gov.ng',
              address:
                '3rd Floor, New Federal Secretariat Complex, Phase III, Central Business District, FCT Abuja, Nigeria.',
              image: userIcon,
            },
          ],
        },
      ],
    };
  },
};
</script>

<style scoped>
.contact-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2.5rem 1rem 4.5rem;
  display: grid;
  gap: 1.75rem;
}

.contact-hero {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(260px, 360px);
  gap: 1.75rem;
  padding: 2.2rem;
  border-radius: 36px;
  background: radial-gradient(circle at top right, rgba(42, 138, 67, 0.16), transparent 34%),
    radial-gradient(circle at bottom left, rgba(27, 86, 45, 0.1), transparent 28%),
    linear-gradient(135deg, rgba(255, 255, 255, 0.99), rgba(238, 246, 240, 0.98));
  border: 1px solid rgba(29, 70, 44, 0.08);
  box-shadow: 0 28px 50px rgba(17, 47, 28, 0.08);
}

.contact-hero__eyebrow,
.overview-card__label,
.contact-group__kicker,
.contact-card__position,
.contact-group__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.contact-hero__eyebrow {
  padding: 0.4rem 0.8rem;
  border-radius: 999px;
  background: rgba(42, 138, 67, 0.1);
  color: #2a8a43;
}

.contact-hero__copy h1 {
  margin: 1rem 0 0.8rem;
  font-size: clamp(2.4rem, 4vw, 3.9rem);
  line-height: 0.98;
  letter-spacing: -0.04em;
  color: #163424;
}

.contact-hero__copy p,
.overview-card p,
.hero-card__label + strong + p {
  margin: 0;
  max-width: 65ch;
  color: #5a7465;
  line-height: 1.75;
  font-size: 1rem;
}

.contact-hero__card,
.overview-card,
.contact-card {
  border-radius: 28px;
  border: 1px solid rgba(29, 70, 44, 0.08);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.97), rgba(247, 250, 248, 0.94));
  box-shadow: 0 18px 34px rgba(21, 55, 33, 0.05);
}

.contact-hero__card {
  padding: 1.55rem;
  align-self: stretch;
  display: grid;
  gap: 0.7rem;
}

.hero-card__label,
.overview-card__label,
.contact-group__kicker {
  color: #2a8a43;
}

.contact-hero__card strong {
  color: #173425;
  font-size: 1.2rem;
}

.contact-overview {
  display: block;
}

.overview-card {
  padding: 1.55rem 1.65rem;
}

.overview-card--wide h2 {
  margin: 0.55rem 0 0.75rem;
  color: #173425;
  font-size: clamp(1.4rem, 2.5vw, 2rem);
}

.contact-group {
  display: grid;
  gap: 1.15rem;
}

.contact-group__header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: end;
  padding: 0 0.2rem;
}

.contact-group__header h2 {
  margin: 0.45rem 0 0;
  color: #173425;
  font-size: clamp(1.35rem, 2vw, 1.8rem);
}

.contact-group__count {
  padding: 0.45rem 0.85rem;
  border-radius: 999px;
  background: rgba(42, 138, 67, 0.1);
  color: #3f5f4c;
}

.contact-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.15rem;
}

.contact-card {
  padding: 1.4rem;
  display: grid;
  gap: 1.2rem;
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
}

.contact-card:hover {
  transform: translateY(-3px);
  border-color: rgba(42, 138, 67, 0.16);
  box-shadow: 0 24px 40px rgba(21, 55, 33, 0.08);
}

.contact-card__identity {
  display: flex;
  gap: 1rem;
  align-items: center;
  min-width: 0;
}

.contact-card__avatar {
  width: 78px;
  height: 78px;
  border-radius: 24px;
  object-fit: cover;
  background: radial-gradient(circle at top right, rgba(42, 138, 67, 0.1), transparent 40%),
    linear-gradient(180deg, #f7faf8, #edf3ee);
  padding: 0.8rem;
  border: 1px solid rgba(29, 70, 44, 0.08);
}

.contact-card__position {
  color: #2a8a43;
}

.contact-card h3 {
  margin: 0.45rem 0 0.2rem;
  color: #173425;
  font-size: 1.28rem;
  overflow-wrap: anywhere;
}

.contact-card__department {
  margin: 0;
  color: #5d7467;
  overflow-wrap: anywhere;
}

.contact-card__meta {
  display: grid;
  gap: 0.95rem;
  margin: 0;
}

.contact-card__meta div {
  display: grid;
  gap: 0.25rem;
  padding-top: 0.7rem;
  border-top: 1px solid rgba(29, 70, 44, 0.08);
}

.contact-card__meta div:first-child {
  padding-top: 0;
  border-top: 0;
}

.contact-card__meta dt {
  color: #4e6a58;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.contact-card__meta dd {
  margin: 0;
  color: #183726;
  line-height: 1.65;
  overflow-wrap: anywhere;
}

.contact-card__meta a {
  color: #1d6f37;
  text-decoration: none;
}

.contact-card__meta a:hover {
  text-decoration: underline;
}

@media (max-width: 991px) {
  .contact-page {
    padding-top: clamp(11.5rem, 21vw, 13rem);
  }

  .contact-hero,
  .contact-grid {
    grid-template-columns: 1fr;
  }

  .contact-hero {
    padding: 1.8rem;
  }

  .contact-hero__card {
    max-width: 100%;
  }

  .contact-group__header {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (max-width: 640px) {
  .contact-page {
    gap: 1.25rem;
    padding: clamp(12.5rem, 28vw, 14rem) 0.85rem 3.5rem;
  }

  .contact-hero,
  .overview-card,
  .contact-card {
    padding: 1.2rem;
  }

  .contact-hero {
    gap: 1.2rem;
    border-radius: 28px;
  }

  .contact-hero__copy h1 {
    font-size: clamp(2rem, 10vw, 2.6rem);
    line-height: 1.02;
  }

  .contact-hero__copy p,
  .overview-card p,
  .hero-card__label + strong + p {
    font-size: 0.95rem;
    line-height: 1.65;
  }

  .overview-card--wide h2,
  .contact-group__header h2 {
    line-height: 1.2;
  }

  .contact-group__count {
    padding: 0.4rem 0.75rem;
  }

  .contact-card__identity {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.8rem;
  }

  .contact-card__avatar {
    width: 68px;
    height: 68px;
    border-radius: 20px;
    padding: 0.7rem;
  }

  .contact-card h3 {
    font-size: 1.1rem;
  }
}

@media (max-width: 420px) {
  .contact-page {
    padding: 13rem 0.75rem 3.25rem;
  }

  .contact-hero,
  .overview-card,
  .contact-card,
  .contact-hero__card {
    padding: 1rem;
  }

  .contact-hero__eyebrow,
  .overview-card__label,
  .contact-group__kicker,
  .contact-card__position,
  .contact-group__count {
    letter-spacing: 0.12em;
    font-size: 0.68rem;
  }
}
</style>
